/**
 * Auth storage utilities for beatly-omni-app.
 * Shape matches beatly-web-app auth storage for compatibility.
 */

/** Subset of v2Team.account_manager from GET /app/v2/user (client teams). */
export interface AuthAccountManager {
  name: string;
  email: string;
  picture_url?: string;
  phone?: string;
  slack_url?: string;
  msteams_url?: string;
  whatsapp_url?: string;
}

/** Minimal api-v2 user shape (GET /app/v2/user). Any valid user may log in (clients and influencers). */
export interface AuthUser {
  user_id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  phone_is_verified?: boolean;
  language?: string;
  want_email_notifications?: boolean;
  want_sms_notifications?: boolean;
  is_client?: boolean;
  is_influencer?: boolean;
  /** Markets where this user runs marketing campaigns (ISO 3166-1 alpha-2). */
  campaign_countries?: string[];
  /** When true (api-v2 staff flag), omni shows Cowork/Chat and related nav. */
  show_cowork?: boolean;
  /** When true (api-v2 staff flag), omni shows Trends and Evolve nav. */
  show_evolve?: boolean;
  team?: {
    name?: string;
    team_id?: string;
    account_manager?: AuthAccountManager;
    /** When true, team can use mention monitoring (api-v2 GET /app/v2/user). */
    has_mention_enabled?: boolean;
    /** When true, team can connect Meta/TikTok ad accounts (api-v2 GET /app/v2/user). */
    has_ad_accounts_enabled?: boolean;
  };
  client?: { company_name?: string };
  [key: string]: unknown;
}

export interface AuthStorage {
  token?: string;
  user?: AuthUser;
  start_url?: string;
  code?: string;
  target?: string;
  language?: string;
}

const AUTH_KEY = 'auth';

export function getAuthFromStorage(): AuthStorage {
  try {
    const authData = localStorage.getItem(AUTH_KEY);
    return authData ? (JSON.parse(authData) as AuthStorage) : {};
  } catch {
    return {};
  }
}

export function updateAuthInStorage(updates: Partial<AuthStorage>): void {
  const currentAuth = getAuthFromStorage();
  const updatedAuth = { ...currentAuth, ...updates };
  localStorage.setItem(AUTH_KEY, JSON.stringify(updatedAuth));
}

export function clearAuthFromStorage(): void {
  localStorage.removeItem(AUTH_KEY);
}

export function getTokenFromStorage(): string | undefined {
  const auth = getAuthFromStorage();
  return auth.token;
}

export function getUserFromStorage(): AuthUser | undefined {
  const auth = getAuthFromStorage();
  return auth.user;
}

/**
 * Clear auth storage and redirect to /login. Use from TopNavBar or any component.
 */
export function logout(): void {
  // Signing out also ends this person's phone review codes (they act with this session). Best effort, never blocks.
  const token = getTokenFromStorage();
  const cowork = import.meta.env.VITE_COWORK_API_URL as string | undefined;
  if (token && cowork) { try { void fetch(`${cowork}/v1/review-passes/revoke`, { method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${token}` } }).catch(() => {}); } catch { /* offline */ } }
  clearAuthFromStorage();
  window.location.href = '/login';
}
